import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Autocomplete, Box, Typography, IconButton, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "minimal-shared";
import { Iconify } from "../../Epos/../../Minimal/src/components/iconify/iconify";
import { getProductsList } from "../../../../data/epos";
import { getOrderTypeOptions } from "../utils/addProductHelpers";
import "../../../common/commonStyles.css";

const AddProductDialog = ({ open, onClose, customerId, orderChannel, onSave }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOrderType, setSelectedOrderType] = useState(null);
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [productSearchInput, setProductSearchInput] = useState("");
  const [errors, setErrors] = useState({ product: "", orderType: "", price: "", quantity: "" });

  const debouncedProductSearch = useDebounce(productSearchInput, 500);

  const { data: productList, isLoading: isLoadingProductList } = useQuery({
    queryKey: ["productList", debouncedProductSearch, customerId, orderChannel],
    queryFn: () => getProductsList({
      pattern: debouncedProductSearch,
      customer_id: customerId,
      orderChannel: orderChannel
    }),
    enabled: !!customerId && debouncedProductSearch.length >= 2,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
  const getOrderTypeOptions = (product) => {
    if (!product) return [];
    const split = Number(product.split);
    const options = [];
    if (split === 0) {
      options.push({ id: 0, title: "Box", value: "Box" });
    } else if (split === 1) {
      options.push({ id: 1, title: "Piece", value: "Piece" });
    } else if (split === 2) {
      options.push({ id: 0, title: "Box", value: "Box" });
      options.push({ id: 1, title: "Piece", value: "Piece" });
    }
    return options;
  };


  const productOptions = productList?.product_details || [];

  const orderTypeOptions = getOrderTypeOptions(selectedProduct);

  useEffect(() => {
    if (orderTypeOptions.length > 0 && !selectedOrderType) {
      setSelectedOrderType(orderTypeOptions[0]);
      if (selectedProduct) {
        const boxPrice = Number(selectedProduct.final_price) || 0;
        const piecePrice = Number(selectedProduct.final_split_price) || 0;
        const defaultPrice = orderTypeOptions[0].value === "Piece" ? piecePrice : boxPrice;
        setPrice(defaultPrice > 0 ? defaultPrice.toFixed(2) : "");
      }
    }
  }, [orderTypeOptions, selectedOrderType, selectedProduct]);

  const validateQuantity = (value) => {
    if (!value || value === "") {
      return "Quantity is required";
    }
    const numValue = Number(value);
    if (!Number.isInteger(numValue) || numValue < 0) {
      return "Please enter a valid quantity";
    }
    if (numValue < 1 || numValue > 9999) {
      return "Quantity must be between 1 and 9999";
    }
    return "";
  };

  const validatePrice = (value) => {
    if (!value || value === "") {
      return "Price is required";
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return "Please enter a valid price (e.g., 45.99)";
    }
    const decimalPlaces = (value.toString().split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      return "Please enter a valid price (e.g., 45.99)";
    }
    if (numValue < 0.01 || numValue > 9999.99) {
      return "Price must be between £0.01 and £9,999.99";
    }
    return "";
  };

  const handleProductChange = (event, newValue) => {
    setSelectedProduct(newValue);
    setSelectedOrderType(null);
    setErrors({ ...errors, product: "" });
    if (!newValue) {
      setPrice("");
    }
  };

  const handleOrderTypeChange = (event, newValue) => {
    setSelectedOrderType(newValue);
    setErrors({ ...errors, orderType: "" });
    if (newValue && selectedProduct) {
      const boxPrice = Number(selectedProduct.final_price) || 0;
      const piecePrice = Number(selectedProduct.final_split_price) || 0;
      const defaultPrice = newValue.value === "Piece" ? piecePrice : boxPrice;
      setPrice(defaultPrice > 0 ? defaultPrice.toFixed(2) : "");
    }
  };

  const handlePriceChange = (e) => {
    setPrice(e.target.value);
    if (errors.price) {
      const error = validatePrice(e.target.value);
      setErrors({ ...errors, price: error });
    }
  };

  const handleQuantityChange = (e) => {
    setQuantity(e.target.value);
    if (errors.quantity) {
      const error = validateQuantity(e.target.value);
      setErrors({ ...errors, quantity: error });
    }
  };

  const handleSave = (e) => {
    if (e) {
      e.preventDefault();
    }

    const newErrors = {
      product: !selectedProduct ? "Product is required" : "",
      orderType: !selectedOrderType ? "Order type is required" : "",
      price: validatePrice(price),
      quantity: validateQuantity(quantity)
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(error => error !== "")) {
      return;
    }

    const originalBoxPrice = Number(selectedProduct.price ?? 0) || 0;
    const originalPiecePrice = Number(selectedProduct.split_price ?? 0) || 0;
    const originalPrice = selectedOrderType.value === "Piece" ? originalPiecePrice : originalBoxPrice;

    onSave?.({ selectedProduct, selectedOrderType, price, quantity, original_price: originalPrice });
    handleClose();
  };

  const handleClose = () => {
    setSelectedProduct(null);
    setSelectedOrderType(null);
    setPrice("");
    setQuantity("");
    setProductSearchInput("");
    setErrors({ product: "", orderType: "", price: "", quantity: "" });
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: -2}}>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 500 }}>Add Product</Typography>
          <IconButton onClick={handleClose} size="small">
            <Iconify icon="mdi:close" />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box component="form" onSubmit={handleSave} noValidate>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 12 }}>
              <Autocomplete
                fullWidth
                autoFocus
                options={productOptions}
                getOptionLabel={(option) => `${option.product_code} - ${option.product_name}`}
                value={selectedProduct}
                onChange={handleProductChange}
                inputValue={productSearchInput}
                onInputChange={(event, newInputValue) => setProductSearchInput(newInputValue)}
                loading={isLoadingProductList}
                noOptionsText={
                  !customerId
                    ? "Please select a customer first"
                    : productSearchInput.length < 2
                      ? "Type at least 2 characters to search"
                      : "No products found"
                }
                renderInput={(params) => (
                  <TextField
                    className="form-input-border"
                    {...params}
                    label={<span>Select Product <span style={{ color: 'red' }}>*</span></span>}
                    placeholder="Type to search products"
                    error={!!errors.product}
                    helperText={errors.product}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingProductList ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.product_code} - {option.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Box: £{Number(option.final_price || 0).toFixed(2)} | Piece: £{Number(option.final_split_price || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Autocomplete
                fullWidth
                openOnFocus
                options={orderTypeOptions}
                getOptionLabel={(option) => option.title}
                value={selectedOrderType}
                onChange={handleOrderTypeChange}
                disabled={!selectedProduct}
         
                renderInput={(params) => (
                  <TextField
                    className="form-input-border"
                    {...params}
                    label={<span>Order Type <span style={{ color: 'red' }}>*</span></span>}
                    placeholder="Select order type"
                    error={!!errors.orderType}
                    helperText={errors.orderType}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.title}
                  </li>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <TextField
                className="form-input-border"
                fullWidth
                label={<span>Price <span style={{ color: 'red' }}>*</span></span>}
                placeholder="Enter price"
                value={price}
                onChange={handlePriceChange}
                error={!!errors.price}
                helperText={errors.price}
                inputProps={{
                  step: "0.01",
                  min: "0.01",
                  max: "9999.99"
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <TextField
                className="form-input-border"
                fullWidth
                label={<span>QTY <span style={{ color: 'red' }}>*</span></span>}
                placeholder="Enter quantity"
                value={quantity}
                onChange={handleQuantityChange}
                error={!!errors.quantity}
                helperText={errors.quantity}
                inputProps={{
                  min: "1",
                  max: "9999"
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined"
           className="button-variant-outlined"
           type="button" 
           onClick={handleClose}
           >Cancel</Button>
          <Button variant="contained" 
          className="button-variant-contained"
           type="submit">Save Changes</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddProductDialog;


